import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './Home.module.css';
import ReactPlayer from 'react-player';
import { Link } from "react-router-dom";
import { IoCloseOutline } from 'react-icons/io5';
import { trackModal, trackVideoInteraction, trackExternalLink, trackNavigation } from '../utils/analytics';

const Home = () => {
  const lillyYoutubeUrl = 'https://www.youtube.com/watch?v=89i4MvTdh8c';
  const lillyYoutubeThumbnail = 'https://i.ytimg.com/vi/89i4MvTdh8c/maxresdefault.jpg';
  const springYoutubeUrl = 'https://www.youtube.com/watch?v=732w9e0MXzA';
  const experimentalFeatureUrl = 'https://youtu.be/UPsB9aV8NXE';
  const argyleVimeoUrl = 'https://vimeo.com/1162358355/64eafc308d';
  const [argyleThumbnailUrl, setArgyleThumbnailUrl] = useState('');

  useEffect(() => {
    fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(argyleVimeoUrl)}`)
      .then(r => r.json())
      .then(data => { if (data.thumbnail_url) setArgyleThumbnailUrl(data.thumbnail_url.replace(/_\d+x\d+(\.\w+)$/, '_1280x720$1')); })
      .catch(() => {});
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLillyModalOpen, setIsLillyModalOpen] = useState(false);
  const [isArgyleModalOpen, setIsArgyleModalOpen] = useState(false);
  const [isFeatureHovered, setIsFeatureHovered] = useState(false);
  const [isFeatureLocked, setIsFeatureLocked] = useState(false);
  const [isFeatureExpanded, setIsFeatureExpanded] = useState(false);
  const [featureVolume, setFeatureVolume] = useState(0);
  const [mobileSoundOn, setMobileSoundOn] = useState(false);
  const [coarsePointer, setCoarsePointer] = useState(false);
  const featureVideoRef = useRef(null);
  const featurePlayerRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(pointer: coarse)');
    const apply = () => setCoarsePointer(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const openModal = () => {
    trackModal('open', 'Spring Video');
    setIsModalOpen(true);
  };
  const closeModal = () => {
    trackModal('close', 'Spring Video');
    setIsModalOpen(false);
  };
  const openLillyModal = () => {
    trackModal('open', 'Lilly Lives Alone Video');
    setIsLillyModalOpen(true);
  };
  const closeLillyModal = () => {
    trackModal('close', 'Lilly Lives Alone Video');
    setIsLillyModalOpen(false);
  };
  const openArgyleModal = () => {
    trackModal('open', 'Argyle Video');
    setIsArgyleModalOpen(true);
  };
  const closeArgyleModal = () => {
    trackModal('close', 'Argyle Video');
    setIsArgyleModalOpen(false);
  };
  const closeFeatureVideo = () => {
    setIsFeatureExpanded(false);
    setIsFeatureLocked(false);
  };
  const openFeatureVideo = () => {
    const videoElement = featureVideoRef.current;
    if (!videoElement || isFeatureLocked) return;

    const rect = videoElement.getBoundingClientRect();
    const targetWidth = Math.min(window.innerWidth * 0.88, 1180);
    const targetHeight = targetWidth * 9 / 16;
    const targetLeft = (window.innerWidth - targetWidth) / 2;
    const targetTop = (window.innerHeight - targetHeight) / 2;

    videoElement.style.setProperty('--feature-top', `${rect.top}px`);
    videoElement.style.setProperty('--feature-left', `${rect.left}px`);
    videoElement.style.setProperty('--feature-width', `${rect.width}px`);
    videoElement.style.setProperty('--feature-height', `${rect.height}px`);
    videoElement.style.setProperty('--feature-target-top', `${targetTop}px`);
    videoElement.style.setProperty('--feature-target-left', `${targetLeft}px`);
    videoElement.style.setProperty('--feature-target-width', `${targetWidth}px`);
    videoElement.style.setProperty('--feature-target-height', `${targetHeight}px`);

    setIsFeatureLocked(true);
    setIsFeatureExpanded(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setIsFeatureExpanded(true));
    });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setFeatureVolume((currentVolume) => {
        const targetVolume = coarsePointer
          ? (mobileSoundOn ? 0.8 : 0)
          : (isFeatureHovered || isFeatureLocked ? 0.8 : 0);
        const step = 0.012;

        if (currentVolume === targetVolume) {
          return currentVolume;
        }

        if (currentVolume < targetVolume) {
          return Math.min(targetVolume, Number((currentVolume + step).toFixed(2)));
        }

        return Math.max(targetVolume, Number((currentVolume - step).toFixed(2)));
      });
    }, 40);

    return () => clearInterval(interval);
  }, [isFeatureHovered, isFeatureLocked, mobileSoundOn, coarsePointer]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeFeatureVideo();
        closeArgyleModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className={styles.home}>
      <button
        type="button"
        className={styles.featureBackdrop}
        data-active={isFeatureHovered || isFeatureLocked ? 'true' : undefined}
        data-locked={isFeatureLocked ? 'true' : undefined}
        onClick={closeFeatureVideo}
        aria-label="Close experimental video"
        tabIndex={isFeatureLocked ? 0 : -1}
      />
      <section className={styles.introRow} aria-labelledby="home-intro-heading">
        <div className={styles.projectMedia}>
          <div
            ref={featureVideoRef}
            className={styles.topVideo}
            data-hover={isFeatureHovered && !isFeatureLocked ? 'true' : undefined}
            data-focus={isFeatureLocked ? 'true' : undefined}
            data-expanded={isFeatureExpanded ? 'true' : undefined}
            aria-label="Experimental video"
            onMouseEnter={coarsePointer ? undefined : () => setIsFeatureHovered(true)}
            onMouseLeave={coarsePointer ? undefined : () => setIsFeatureHovered(false)}
            onFocus={coarsePointer ? undefined : () => setIsFeatureHovered(true)}
            onBlur={coarsePointer ? undefined : () => setIsFeatureHovered(false)}
            onClick={coarsePointer ? undefined : openFeatureVideo}
            tabIndex={coarsePointer ? -1 : 0}
          >
            <ReactPlayer
              ref={featurePlayerRef}
              url={experimentalFeatureUrl}
              playing
              loop
              muted={featureVolume < 0.01}
              volume={featureVolume}
              playsinline
              width="100%"
              height="100%"
              controls={false}
              config={{
                youtube: {
                  playerVars: {
                    autoplay: 1,
                    controls: 0,
                    disablekb: 1,
                    fs: 0,
                    iv_load_policy: 3,
                    modestbranding: 1,
                    playsinline: 1,
                    rel: 0,
                    showinfo: 0,
                  },
                },
              }}
              className={styles.topVideoPlayer}
            />
            {coarsePointer && (
              <button
                type="button"
                className={`${styles.mobileSpeaker} ${mobileSoundOn ? styles.mobileSpeakerOn : ''}`}
                aria-label={mobileSoundOn ? 'Mute' : 'Unmute'}
                onClick={() => {
                  const internal = featurePlayerRef.current?.getInternalPlayer();
                  if (!mobileSoundOn) {
                    internal?.unMute?.();
                    internal?.setVolume?.(80);
                  } else {
                    internal?.mute?.();
                  }
                  setMobileSoundOn(v => !v);
                }}
              >
                {mobileSoundOn ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                    <line x1="23" y1="9" x2="17" y2="15"/>
                    <line x1="17" y1="9" x2="23" y2="15"/>
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
        <div className={styles.introCopy}>
          <p className={styles.introEyebrow}>Composer · producer · sound artist</p>
          <p className={styles.introLead}>
            Portland-based composer, producer, and multi-instrumentalist working across film, records, theater, podcasts, and experimental media.
          </p>
          <p className={styles.introMeta}>
            Organic textures, electronic detail, and jazz-rooted listening shaped into music for story, space, and screen.
          </p>
        </div>
      </section>

      <div className={styles.header}>
        LATEST
      </div>

      <section className={styles.projectRow} aria-labelledby="home-lilly-heading">
        <div className={styles.projectMedia}>
          <button
            type="button"
            className={styles.videoPreviewButton}
            onClick={() => {
              trackVideoInteraction('play', 'Lilly Lives Alone', 'Home');
              openLillyModal();
            }}
            aria-label="Play Lilly Lives Alone trailer"
          >
            <img
              src={lillyYoutubeThumbnail}
              alt="Lilly Lives Alone trailer thumbnail"
              className={styles.videoPreviewImage}
            />
            <span className={styles.videoPreviewPlay}>▶</span>
          </button>
        </div>
        <div className={styles.projectCopy}>
          <p className={styles.projectEyebrow}>Feature film · score</p>
          <h2 id="home-lilly-heading" className={styles.title}>
            Lilly Lives Alone
          </h2>
          <p className={styles.projectLead}>
            The ten year anniversary of a tragic accident sends Lilly&apos;s guilt-ridden mind over the edge,
            plunging her into a drug and booze fueled fever dream with supernatural terrors.
          </p>
          <p className={styles.projectMeta}>
            Written and directed by Martin Melnick · music by Benjamin Cleek
          </p>
          <a
            href="https://darkskyfilms.com/film/lilly-lives-alone/"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.projectInlineLink}
            onClick={() => trackExternalLink('https://darkskyfilms.com/film/lilly-lives-alone/', 'Lilly Lives Alone — film page')}
          >
            Film page at Dark Sky Films
          </a>
        </div>
      </section>

      <section className={styles.projectRow} aria-labelledby="home-argyle-heading">
        <div className={styles.projectMedia}>
          <button
            type="button"
            className={styles.videoPreviewButton}
            onClick={() => {
              trackVideoInteraction('play', 'Argyle', 'Home');
              openArgyleModal();
            }}
            aria-label="Play Argyle video"
          >
            <img
              src={argyleThumbnailUrl}
              alt="Argyle video thumbnail"
              className={styles.videoPreviewImage}
            />
            <span className={styles.videoPreviewPlay}>▶</span>
          </button>
        </div>
        <div className={styles.projectCopy}>
          <p className={styles.projectEyebrow}>Film · score</p>
          <h2 id="home-argyle-heading" className={styles.title}>
            Argyle
          </h2>
          <p className={styles.projectLead}>
            Original music for Galago Pictures&apos; Argyle.
          </p>
          <p className={styles.projectMeta}>
            Music by Benjamin Cleek
          </p>
          <a
            href="https://www.galagopictures.com/argyle"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.projectInlineLink}
            onClick={() => trackExternalLink('https://www.galagopictures.com/argyle', 'Argyle — Galago Pictures')}
          >
            Galago Pictures
          </a>
        </div>
      </section>

      <section className={styles.projectRow} aria-labelledby="home-damp-heading">
        <div className={styles.projectMedia}>
          <div className={styles.projectMediaStack}>
            <iframe
              src="https://open.spotify.com/embed/episode/2394KDHt8wJyqFZmQdcWoL?utm_source=generator"
              className={styles.spotifyEmbed}
              frameBorder="0"
              allowFullScreen
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              title="Damp January Spotify embed"
            />
          </div>
        </div>
        <div className={styles.projectCopy}>
          <p className={styles.projectEyebrow}>Podcast series · Live Wire Radio</p>
          <h2 id="home-damp-heading" className={styles.title}>
            Damp January
          </h2>
          <p className={styles.projectLead}>
            A limited-run special with Luke Burbank, with original music and sonic identity woven through the arc of the series.
          </p>
          <p className={styles.projectMeta}>
            Original music by Benjamin Cleek
          </p>
        </div>
      </section>

      <div className={styles.header}>
        Produced By
      </div>

      <div className={styles.albumGrid} aria-label="Produced By albums">
        <a
          href="https://open.spotify.com/album/3lWaKsq5qdMZfdeibLqpBl?si=81VFBeohSfm3LvKf086L8g"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.albumMediaLink}
          onClick={() => trackExternalLink('https://open.spotify.com/album/3lWaKsq5qdMZfdeibLqpBl?si=81VFBeohSfm3LvKf086L8g', 'Taylor Appelo - Animal cover')}
        >
          <div className={styles.albumImageWrapper}>
            <img
              src="https://benjamincleek.s3.us-west-2.amazonaws.com/photos/108a133b-ed00-4a9d-863a-883cae591b45-Taylor%20Appelo%20-%20Animal.png"
              alt="Taylor Appelo — Animal album cover"
              className={styles.albumImageRow}
            />
          </div>
          <div className={styles.albumCardInfo}>
            <p className={styles.albumCardName}>Animal</p>
            <p className={styles.albumCardMeta}>by: Taylor Appelo</p>
            <p className={styles.albumCardMeta}>produced by: Benjamin</p>
          </div>
        </a>
        <a
          href="https://open.spotify.com/album/4nae493gDwLmnzgFHOfCm5?si=UeRxgAUESQazwA4z3D2QXA"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.albumMediaLink}
          onClick={() => trackExternalLink('https://open.spotify.com/album/4nae493gDwLmnzgFHOfCm5?si=UeRxgAUESQazwA4z3D2QXA', 'Illya - Midnight Catches 2025 cover')}
        >
          <div className={styles.albumImageWrapper}>
            <img
              src="https://benjamincleek.s3.us-west-2.amazonaws.com/photos/e7adb590-e6ab-407a-afee-484393cf7037-Illya%20-%20Midnight%20Catches%202025%20Screen%20Shot%202025-02-01%20at%205.44.12%20PM.png"
              alt="Illya — Midnight Catches 2025 album cover"
              className={styles.albumImageRow}
            />
          </div>
          <div className={styles.albumCardInfo}>
            <p className={styles.albumCardName}>Midnight Catches</p>
            <p className={styles.albumCardMeta}>by: Illya deTorres</p>
            <p className={styles.albumCardMeta}>produced by: Benjamin</p>
          </div>
        </a>
        <a
          href="https://open.spotify.com/album/69oQKwLh98k7Sm2QhNEOUO?si=c5OqsuafQIG5RWi9BUUhFQ"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.albumMediaLink}
          onClick={() => trackExternalLink('https://open.spotify.com/album/69oQKwLh98k7Sm2QhNEOUO?si=c5OqsuafQIG5RWi9BUUhFQ', 'Melissa Dollar - Weather The Storms cover')}
        >
          <div className={styles.albumImageWrapper}>
            <img
              src="https://benjamincleek.s3.us-west-2.amazonaws.com/photos/eeda35d9-62ac-45ab-bc99-4117adb048cb-Melissa%20Dollar%20-%20Weather%20The%20Storm.jpg"
              alt="Melissa Dollar — Weather The Storms album cover"
              className={styles.albumImageRow}
            />
          </div>
          <div className={styles.albumCardInfo}>
            <p className={styles.albumCardName}>Weather The Storms</p>
            <p className={styles.albumCardMeta}>by: Melissa Dollar</p>
            <p className={styles.albumCardMeta}>produced by: Benjamin</p>
          </div>
        </a>
        <a
          href="https://open.spotify.com/album/1UyI9mHSPdfpanT5D0boDJ?si=yyn1kNu_SCOcOFVBK7XUlQ"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.albumMediaLink}
          onClick={() => trackExternalLink('https://open.spotify.com/album/1UyI9mHSPdfpanT5D0boDJ?si=yyn1kNu_SCOcOFVBK7XUlQ', 'David Van Auken - American Harmony cover')}
        >
          <div className={styles.albumImageWrapper}>
            <img
              src="https://benjamincleek.s3.us-west-2.amazonaws.com/photos/5d603355-c3d4-4f02-996a-265041ffab37-American%20Harmoney%20-%20David%20Van%20Auken.jpg"
              alt="David Van Auken — American Harmony album cover"
              className={styles.albumImageRow}
            />
          </div>
          <div className={styles.albumCardInfo}>
            <p className={styles.albumCardName}>American Harmony</p>
            <p className={styles.albumCardMeta}>by: David Van Auken</p>
            <p className={styles.albumCardMeta}>produced by: Benjamin</p>
          </div>
        </a>
      </div>

      <div className={styles.header}>
        RECENT WORKS
      </div>

      <section className={styles.projectRow} aria-labelledby="home-spring-heading">
        <div className={styles.projectMedia}>
          <button
            type="button"
            className={styles.springThumbButton}
            onClick={() => {
              trackVideoInteraction('play', 'Spring', 'Home');
              openModal();
            }}
            aria-label="Play Spring short film"
          >
            <img
              src="https://benjamincleek.s3.us-west-2.amazonaws.com/photos/affe197d-fed1-4020-ad28-6fd22a6b9383-spring%20-%20ai%20upscaled%20enhanced.jpg"
              alt="Spring — video thumbnail"
              className={styles.springThumbImage}
            />
            <span className={styles.videoPreviewPlay}>▶</span>
          </button>
        </div>
        <div className={styles.projectCopy}>
          <p className={styles.projectEyebrow}>Short film · score</p>
          <h2 id="home-spring-heading" className={styles.title}>
            Spring
          </h2>
          <p className={styles.projectLead}>
            A short film score built around renewal, movement, and gradual transformation.
          </p>
          <p className={styles.projectMeta}>
            Music by Benjamin Cleek
          </p>
        </div>
      </section>

      <section className={styles.projectRow} aria-labelledby="home-tfaaf-heading">
        <div className={styles.projectMedia}>
          <Link to="/tfaaf" onClick={() => trackNavigation('Two Funerals and a Freezer poster', '/tfaaf')}>
            <img
              src="https://benjamincleek.s3.us-west-2.amazonaws.com/photos/b66d7765-2be4-45dc-8655-455be8127bfc-Screen%20Shot%202024-03-02%20at%2010.07.11%20AM.png"
              alt="Poster for Two Funerals and a Freezer"
              className={styles.tfaafPoster}
            />
          </Link>
        </div>
        <div className={styles.projectCopy}>
          <p className={styles.projectEyebrow}>Short film · score</p>
          <h2 id="home-tfaaf-heading" className={styles.title}>
            Two Funerals and a Freezer
          </h2>
          <p className={styles.projectLead}>
            Score and sonic treatment for Melissa Fisher&apos;s short—still gallery, Vimeo cuts, and a protected screener live on the dedicated project page.
          </p>
          <p className={styles.projectMeta}>
            Directed by Melissa Fisher · music by Benjamin Cleek
          </p>
          <Link
            to="/tfaaf"
            className={styles.projectInlineLink}
            onClick={() => trackNavigation('Two Funerals and a Freezer', '/tfaaf')}
          >
            Open project page
          </Link>
        </div>
      </section>

      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal} role="presentation">
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Spring video">
            <ReactPlayer
              url={springYoutubeUrl}
              playing
              controls
              playsinline
              width="100%"
              height="100%"
              config={{
                youtube: {
                  playerVars: {
                    playsinline: 1,
                    modestbranding: 1,
                    rel: 0,
                  },
                },
              }}
              className={styles.reactPlayer}
            />
          </div>
        </div>
      )}

      {isLillyModalOpen && (
        <div className={styles.modalOverlay} onClick={closeLillyModal} role="presentation">
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Lilly Lives Alone trailer">
            <ReactPlayer
              url={lillyYoutubeUrl}
              playing
              controls
              playsinline
              width="100%"
              height="100%"
              config={{
                youtube: {
                  playerVars: {
                    playsinline: 1,
                    modestbranding: 1,
                    rel: 0,
                  },
                },
              }}
              className={styles.reactPlayer}
            />
          </div>
        </div>
      )}

      {isArgyleModalOpen && (
        <div className={styles.modalOverlay} onClick={closeArgyleModal} role="presentation">
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Argyle video">
            <ReactPlayer
              url={argyleVimeoUrl}
              playing
              controls
              playsinline
              width="100%"
              height="100%"
              className={styles.reactPlayer}
            />
            <button onClick={closeArgyleModal} className={styles.closeButton} aria-label="Close Argyle video">
              <IoCloseOutline />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
